import { getFlagColor } from '../../game/world-map/terrial-engine/constants'
import type { CastleData } from '../../game/world-map/terrial-engine/types'

import { getEspionageMapMarkerMetrics } from './espionageV2WorldMapUtils'

const allianceFlagImageByUrl = new Map<string, HTMLImageElement>()
const allianceFlagLoadListeners = new Set<() => void>()

function notifyAllianceFlagLoads() {
  allianceFlagLoadListeners.forEach((fn) => {
    try {
      fn()
    } catch {
      /* ignore */
    }
  })
}

export function subscribeEspionageAllianceFlagLoads(listener: () => void): () => void {
  allianceFlagLoadListeners.add(listener)
  return () => allianceFlagLoadListeners.delete(listener)
}

function getOrCreateAllianceFlagImage(url: string): HTMLImageElement | null {
  const u = url.trim()
  if (!u) return null
  let img = allianceFlagImageByUrl.get(u)
  if (!img) {
    img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => notifyAllianceFlagLoads()
    img.onerror = () => notifyAllianceFlagLoads()
    img.src = u
    allianceFlagImageByUrl.set(u, img)
  }
  return img
}

function flagColorToCss(color: number): string {
  const n = color >>> 0
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  return `rgb(${r}, ${g}, ${b})`
}

function drawAllianceFlagScreen(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  screenX: number,
  screenY: number,
  targetWidth: number,
  flagGap: number,
): void {
  const dw = targetWidth
  const dh = Math.max(8, Math.round((dw * img.naturalHeight) / Math.max(1, img.naturalWidth)))
  const ax = screenX
  const ay = screenY - dh - flagGap

  ctx.save()
  ctx.imageSmoothingEnabled = true
  ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, ax - dw / 2, ay, dw, dh)
  ctx.restore()
}

/** Colored pin marker — alliance flag drawn separately when available. */
export function drawEspionageCastlePin(
  ctx: CanvasRenderingContext2D,
  screenX: number,
  screenY: number,
  markerColor: number,
  options?: { isOwn?: boolean; isSelected?: boolean; zoom?: number },
): void {
  const isOwn = options?.isOwn ?? false
  const isSelected = options?.isSelected ?? false
  const m = getEspionageMapMarkerMetrics(options?.zoom ?? 1)
  const fill = flagColorToCss(markerColor)
  const radius = isOwn ? m.ownPinRadius : m.pinRadius
  const anchorY = screenY - m.pinAnchorLift

  ctx.save()
  ctx.fillStyle = 'rgba(12, 8, 5, 0.85)'
  ctx.beginPath()
  ctx.arc(
    screenX + m.pinShadowOffset,
    anchorY + m.pinShadowOffset,
    radius + 1.2 * m.scale,
    0,
    Math.PI * 2,
  )
  ctx.fill()

  ctx.fillStyle = fill
  ctx.beginPath()
  ctx.arc(screenX, anchorY, radius, 0, Math.PI * 2)
  ctx.fill()

  if (isOwn) {
    ctx.strokeStyle = '#f0d878'
    ctx.lineWidth = m.strokeWidth
    ctx.beginPath()
    ctx.arc(screenX, anchorY, radius + m.ownRingExtra, 0, Math.PI * 2)
    ctx.stroke()
  }

  if (isSelected) {
    ctx.strokeStyle = 'rgba(201, 162, 39, 0.95)'
    ctx.lineWidth = m.strokeWidth
    ctx.beginPath()
    ctx.arc(screenX, anchorY, radius + m.selectedRingExtra, 0, Math.PI * 2)
    ctx.stroke()
  }

  ctx.restore()
}

/** Colored pin marker plus alliance flag image when {@link CastleData.allianceFlagUrl} is set. */
export function drawEspionageCastleFlagScreen(
  ctx: CanvasRenderingContext2D,
  castle: CastleData,
  screenX: number,
  screenY: number,
  options?: { isOwn?: boolean; isSelected?: boolean; markerColor?: number; zoom?: number },
): void {
  const markerColor = options?.markerColor ?? getFlagColor(castle)
  const zoom = options?.zoom ?? 1
  const m = getEspionageMapMarkerMetrics(zoom)

  drawEspionageCastlePin(ctx, screenX, screenY, markerColor, options)

  const allianceUrl = castle.allianceFlagUrl?.trim()
  if (allianceUrl) {
    const img = getOrCreateAllianceFlagImage(allianceUrl)
    if (img?.complete && img.naturalWidth > 0) {
      drawAllianceFlagScreen(ctx, img, screenX, screenY, m.flagWidth, m.flagGap)
    }
  }
}

export function preloadEspionageAllianceFlags(castles: CastleData[]): void {
  for (const castle of castles) {
    const url = castle.allianceFlagUrl?.trim()
    if (url) getOrCreateAllianceFlagImage(url)
  }
}
